/**
 * OnboardingCoachmark (iter77)
 *
 * A one-tap dismissible tooltip that appears once per user for a given
 * `name`. Backing store: `user.coachmarks_seen.<name>` on the profile,
 * dismissed via POST /api/me/coachmarks/{name}/dismiss.
 *
 * Falls back to localStorage so it never re-appears while the profile
 * update is in flight or on offline first-loads.
 *
 * Usage:
 *   <OnboardingCoachmark name="simple_launcher">
 *     Tap any card to get started. Everything else lives one click away.
 *   </OnboardingCoachmark>
 */
import { useEffect, useState } from "react";
import { X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import "./OnboardingCoachmark.css";

const LS_PREFIX = "rally_coachmark_";

export default function OnboardingCoachmark({ name, children, testId }) {
  const { user } = useAuth();
  const lsKey = `${LS_PREFIX}${name}`;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user || !name) return;
    // Respect any of the three signals: profile flag, localStorage flag,
    // or a fresh mount before the auth context has resolved.
    const seenInProfile = user?.coachmarks_seen?.[name];
    let seenLocal = false;
    try { seenLocal = !!window.localStorage.getItem(lsKey); } catch { /* ignore */ }
    if (seenInProfile || seenLocal) return;
    // Small delay so the tooltip animates in AFTER the parent finishes
    // its own fade-up transition — less visual noise on first paint.
    const t = setTimeout(() => setVisible(true), 400);
    return () => clearTimeout(t);
  }, [user, name, lsKey]);

  const dismiss = async () => {
    setVisible(false);
    try { window.localStorage.setItem(lsKey, "1"); } catch { /* ignore */ }
    try { await api.post(`/me/coachmarks/${name}/dismiss`); } catch { /* ignore */ }
  };

  if (!visible) return null;
  return (
    <div className="coachmark" role="alertdialog" aria-live="polite" data-testid={testId || `coachmark-${name}`}>
      <div className="coachmark__body">{children}</div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss tip"
        className="coachmark__close"
        data-testid={`coachmark-${name}-dismiss`}
      >
        <X className="w-3.5 h-3.5" strokeWidth={2} />
      </button>
    </div>
  );
}
